import React, { useEffect, useRef, useState } from "react";
import { Button, CheckBox, DateTimePicker, Form, MultiSelect, Organizational, SelectBox } from "../../components";
import style from "./woundReportForm.module.scss";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { subDays } from "date-fns";
import { convertnewDateToDate, getDateValue, getFacilityLabel } from "../../utils";
import { WOUND_ACQUIRED_OPTIONS } from "../../constants";
import { getWoundDashBoardOverview, getWoundDashBoardWoundcount, setWoundReportPayload } from "../../app/woundReportSlice";
import { optionTypeAsync } from "../../app/patientSlice";
import { getFaciltiestCustomerAsyn } from "../../app/usageAnalyticsReportSlice";
import useFacilityOptions from "../../hooks/useFacilityOptions";

export const WoundReportForm = ({ setTempPayload, selectedDivOption, setSelectedDivOption, setWoundProgressValue, setTabIndex }) => {
  const dispatch = useDispatch();
  const { register, handleSubmit, control, errors, reset } = useForm();

  const woundReportPayload = useSelector((state) => state.woundreport.woundReportPayload);
  const getFacilities = useSelector((state) => state.usageAnalyticsReport.customerFacilities);
  const woundTypeData = useSelector((state) => state.patient.woundType);
  const userDetails = useSelector((state) => state.user);

  const [firstTime, setFirstTime] = useState(true);
  const [startDate, setStartDate] = useState(new Date());
  const [toDateRes, setToDateRes] = useState("");
  const [showApplyState, setShowApplyState] = useState(false);
  const [onselectOrgz, setonselectOrgz] = useState([]);
  const [resetFacility, setResetFacility] = useState(false);
  const [selectedFacilities, setSelectedFacilities] = useState([]);
  const [orgError, setOrgError] = useState(false);
  const [selectedWoundValue, setSelectedWoundValue] = useState(
    woundReportPayload && woundReportPayload.woundType ? woundReportPayload.woundType : []
  );

  const dateRangeRef1 = useRef(null);
  const dateRangeRef2 = useRef(null);
  const woundTypeRef = useRef(null);

  useEffect(() => {
    dispatch(optionTypeAsync("insurancepayer"));
    dispatch(optionTypeAsync("woundType"));
  }, [dispatch]);

  useEffect(() => {
    dispatch(getFaciltiestCustomerAsyn(woundReportPayload.tempWoundCustomer ?? userDetails?.customerNumber));
    let wountCountPayload = {
      ...woundReportPayload,
      optionSelected: woundReportPayload?.optionSelected ? woundReportPayload.optionSelected : "Patients with Wound",
    };
    dispatch(setWoundReportPayload(wountCountPayload));
  }, []);

  const facilities = getFacilities?.data?.facilities ?? [];
  const optionData = useFacilityOptions(facilities);

  const basePayload = ({ fromDate, toDate, facility }) => ({
    fromDate: convertnewDateToDate(fromDate),
    toDate: convertnewDateToDate(toDate),
    facility,
    woundType: "",
    isDischargedPatient: 0,
  });

  const pageOnloadAPI = () => {
    if (!optionData?.length) return;

    const facilitykey = Array.isArray(optionData) ? optionData.map((item) => item.key.split("|")[0].trim()) : [];

    const fromDate = getDateValue("datePicker1");
    const toDate = getDateValue("datePicker2");

    let payload = {
      ...basePayload({ fromDate, toDate, facility: facilitykey }),
      woundAcquired: woundReportPayload?.woundAcquired ?? "all",
    };

    const hasExistingFacility = woundReportPayload?.facility?.length > 0;

    const finalPayload = hasExistingFacility
      ? woundReportPayload
      : {
          ...payload,
          optionSelected: woundReportPayload?.optionSelected ?? "Patients with Wound",
          onselectOrgz: optionData,
        };

    dispatch(getWoundDashBoardOverview(finalPayload));
    dispatch(getWoundDashBoardWoundcount(finalPayload));
    dispatch(setWoundReportPayload(finalPayload));

    setTempPayload(finalPayload);
    setShowApplyState(true);
    setToDateRes(fromDate);
  };

  useEffect(() => {
    if (firstTime && getFacilities?.data?.facilities.length > 0 && document.getElementById("datePicker1") && document.getElementById("datePicker2")) {
      setFirstTime(false);
      pageOnloadAPI();
    }
  }, [firstTime, getFacilities]);

  const customFromDatePickerChange = (e) => {
    const fromDates = new Date(e).getTime();
    const toDates = new Date(document.getElementById("datePicker2").value).getTime();
    setToDateRes(new Date(e));
    setTimeout(() => {
      if (fromDates > toDates) {
        document.getElementById("datePicker2").value = "";
      }
    }, 500);
    setShowApplyState(true);
  };

  const customToDatePickerChange = (e) => {
    setShowApplyState(true);
    const fromDates = new Date(document.getElementById("datePicker1").value).getTime();
    const toDates = new Date(e).getTime();
    setTimeout(() => {
      if (fromDates > toDates) {
        document.getElementById("datePicker1").value = "";
        setToDateRes("");
      }
    }, 500);
  };

  const handleOnBlurFrom = ({ target: { value } }) => {
    const fromDates = new Date(value).getTime();
    const toDates = new Date(document.getElementById("datePicker2").value).getTime();
    setToDateRes(new Date(value));
    setTimeout(() => {
      if (fromDates > toDates) {
        document.getElementById("datePicker2").value = "";
      }
    }, 500);
  };

  const handleOnBlurTO = ({ target: { value } }) => {
    const fromDates = new Date(document.getElementById("datePicker1").value).getTime();
    const toDates = new Date(value).getTime();
    setTimeout(() => {
      if (fromDates > toDates) {
        document.getElementById("datePicker2").value = "";
        setToDateRes("");
      }
    }, 500);
  };

  const onSelectOrg = (selectedList = []) => {
    const hasOptionChange = optionData?.length && optionData.length !== selectedList.length;

    if (hasOptionChange) {
      setShowApplyState(true);
      setResetFacility(false);
    }

    setOrgError(false);

    if (setSelectedFacilities) {
      const facilityKeys = selectedList.map((item) => item.key.split("|")[0].trim());
      setSelectedFacilities(facilityKeys);
    }
    setonselectOrgz(selectedList);
  };

  const handleWoundChange = (e) => {
    setShowApplyState(true);
    const keys = Array.isArray(e) ? e.filter((item) => item.key !== "").map((item) => item.key) : [];
    setSelectedWoundValue(keys);
  };

  const handleShowApplyState = () => {
    setShowApplyState(true);
  };

  const resetData = () => {
    setonselectOrgz([]);
    dispatch(setWoundReportPayload({}));
    reset({ woundType: "", payorType: "", isDischargedPatient: "" });
    woundTypeRef.current.resetSelectedValues([]);
    setSelectedWoundValue("");
    setResetFacility(true);
     setTempPayload("");
     setSelectedDivOption('Patients with Wound');
     setTabIndex(0)
     setWoundProgressValue('new');
    dispatch(setWoundReportPayload({ woundAcquired: "all" }));
    setTimeout(() => {
      setShowApplyState(true);
      setFirstTime(true);
    }, 500);
  };

  const onSubmit = (data) => {
    setSelectedDivOption(woundReportPayload?.optionSelected ?? "Patients with Wound");
    setWoundProgressValue(woundReportPayload?.woundProgress ?? "new");
    if (!selectedFacilities.length) {
      setOrgError(true);
      return;
    }

    setOrgError(false);
    const fromDate = data.datePicker1;
    const toDate = data.datePicker2;

    if (fromDate > toDate) {
      document.getElementById("datePicker2").value = "";
      return;
    }

    let payload = {
      fromDate: convertnewDateToDate(data.datePicker1),
      toDate: convertnewDateToDate(data.datePicker2),
      facility: selectedFacilities,
      woundType: selectedWoundValue,
      woundAcquired: data?.woundAcquired,
      isDischargedPatient: data.isDischargedPatient === "Include Discharged Patients" ? 1 : 0,
    };

    dispatch(getWoundDashBoardOverview(payload));

    let woundCountPayload = {
      ...payload,
      optionSelected: selectedDivOption || "Patients with Wound",
      onselectOrgz,
    };

    const customerEl = document.getElementById("customerNumber");
    if (customerEl?.value) {
      woundCountPayload = {
        ...woundCountPayload,
        tempWoundCustomer: customerEl.value,
      };
    }

    dispatch(getWoundDashBoardWoundcount(woundCountPayload));
    dispatch(setWoundReportPayload(woundCountPayload));
    setTempPayload(woundCountPayload);
    setToDateRes(fromDate);
  };

  return (
    <Form className={style.wound_form} onSubmit={handleSubmit(onSubmit)}>
      <div className={`col-8 ${style.col_spec}`}>
        <label className={`col-5 pl-0 ${style.label_spec}`}>Date Range:</label>
        <DateTimePicker
          id={"datePicker1"}
          name={"datePicker1"}
          control={control}
          classes={{
            date_picker: style.wound_input,
            dateColumn: "col-12 p-0",
            column: `col m-0 ${style.col_spec}`,
          }}
          selected={startDate}
          customOnChange={customFromDatePickerChange}
          defaultValue={woundReportPayload && woundReportPayload.fromDate ? new Date(woundReportPayload.fromDate) : subDays(new Date(startDate), 30)}
          displayIcon={true}
          validator={register()}
          showMonthDropdown={true}
          showYearDropdown={true}
          dateRef={dateRangeRef1}
          errors={errors}
          maxDate={new Date()}
          onBlur={handleOnBlurFrom}
        />
        <DateTimePicker
          id={"datePicker2"}
          name={"datePicker2"}
          control={control}
          classes={{
            date_picker: style.wound_input,
            dateColumn: "col-12 p-0",
            column: `col m-0 pr-0 ${style.col_spec}`,
          }}
          defaultValue={woundReportPayload && woundReportPayload.toDate ? new Date(woundReportPayload.toDate) : new Date()}
          customOnChange={customToDatePickerChange}
          displayIcon={true}
          validator={register()}
          showMonthDropdown={true}
          showYearDropdown={true}
          dateRef={dateRangeRef2}
          minDate={toDateRes ? toDateRes : ""}
          errors={errors}
          maxDate={new Date()}
          onBlur={handleOnBlurTO}
        />
      </div>
      <div className={`col-4 ${style.col_spec}`}></div>
      {optionData && Array.isArray(optionData) && optionData.length === 0 && (
        <Organizational
          id={"facility"}
          name={"facility"}
          label={`Facility/Location:`}
          classes={{
            column: `col-8 ${style.col_spec}`,
            inputClassName: style.wound_select,
            labelClassName: `col-5 ${style.label_spec} pl-0`,
            inputColumn: `col-7 p-0`,
          }}
          optionValue={optionData}
          groupBy="cat"
          displayValue="key"
          defaultValue={Array.isArray(onselectOrgz) ? onselectOrgz : optionData}
          selectAll={true}
          onSelect={(val) => onSelectOrg(val)}
          resetDefault={resetFacility}
          reportView={true}
          customError={orgError}
          required={true}
        />
      )}
      {optionData && Array.isArray(optionData) && optionData.length !== 0 && (
        <Organizational
          id={"facility"}
          name={"facility"}
          label={`Facility/Location:`}
          classes={{
            column: `col-8 ${style.col_spec}`,
            inputClassName: style.wound_select,
            labelClassName: `col-5 ${style.label_spec} pl-0`,
            inputColumn: `col-7 p-0`,
          }}
          optionValue={optionData}
          groupBy="cat"
          displayValue="key"
          defaultValue={
            Array.isArray(woundReportPayload?.onselectOrgz) && woundReportPayload?.onselectOrgz.length > 0
              ? woundReportPayload?.onselectOrgz
              : Array.isArray(onselectOrgz) && onselectOrgz.length > 0
              ? onselectOrgz
              : optionData
          }
          selectAll={true}
          onSelect={(val) => onSelectOrg(val)}
          resetDefault={resetFacility}
          reportView={true}
          customError={orgError}
          required={true}
        />
      )}
      <div className={`col-4 ${style.col_spec}`}></div>
      <MultiSelect
        id={"woundType"}
        name={"woundType"}
        label={"Wound Types:"}
        classes={{
          column: `col-8 ${style.col_spec}`,
          inputClassName: style.wound_select,
          labelClassName: `col-5 ${style.label_spec} pl-0`,
          inputColumn: `col-7 p-0 ${style.select_wrapper}`,
        }}
        optionValue={woundTypeData}
        validator={(e) => {
          register({ required: true });
          woundTypeRef.current = e;
        }}
        placeholder="Select"
        showCheckbox={true}
        displayValue="value"
        onSelect={handleWoundChange}
        onRemove={handleWoundChange}
        labelSubText={"Select all that apply"}
        defaultValue={woundReportPayload && woundReportPayload.woundType ? woundReportPayload.woundType : []}
      />
      <div className={`col-4 ${style.col_spec}`}></div>
      <SelectBox
        id={"woundAcquired"}
        name={"woundAcquired"}
        label={"Wound Acquired:"}
        classes={{
          column: `col-8 ${style.col_spec}`,
          inputClassName: style.wound_select,
          labelClassName: `col-5 ${style.label_spec} pl-0`,
          inputColumn: `col-7 p-0 ${style.select_payortype}`,
        }}
        optionValue={WOUND_ACQUIRED_OPTIONS}
        validator={register}
        placeholder=""
        errors={errors}
        onChange={(e) => handleShowApplyState()}
        defaultValue={woundReportPayload && woundReportPayload.woundAcquired ? woundReportPayload.woundAcquired : "all"}
      />
      {/* <div className={`col-4 ${style.col_spec}`}></div>

      <div className={`col-8 ${style.col_spec} p-0`}>
        <div className="col-6 offset-5 p-0 mb-0">
          <CheckBox
            classes={{ labelClassName: style.custom_control_label }}
            label={"Include Discharged Patients"}
            type={"checkbox"}
            name={"isDischargedPatient"}
            id={"checkBoxId"}
            validator={register}
            onClick={(e) => handleShowApplyState()}
            woundReport={true}
            defaultChecked={woundReportPayload && woundReportPayload.isDischargedPatient === 1 ? true : ""}
          />
        </div>
      </div> */}
      <div className={`col-4 ${style.col_spec}`}></div>
      {showApplyState && (
        <div className={`col-8 ${style.col_spec}`}>
          <label className={`col-5 ${style.label_spec}`}></label>
          <Button classes={{ className: `btn ${style.btn_custom} ${style.btn_small} mr-4` }} type="submit">
            Apply
          </Button>
          <Button classes={{ className: `btn ${style.btn_custom} ${style.btn_small_cancel} ` }} onClick={resetData} type={"button"}>
            Reset
          </Button>
        </div>
      )}
    </Form>
  );
};
